import {
  Fragment,
  memo,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useMemo,
} from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import type { components } from "@/shared/lib/api.types";
import { MY_ROW_TINT } from "@/shared/lib/rowTint";
import { STATUS_GREEN, STATUS_RED } from "@/shared/lib/statusColors";
import { cn } from "@/shared/lib/utils";
import { classColorStyle } from "@/shared/lib/wowClass";
import {
  dayOfMonthLabel,
  isFirstOfMonth,
  isWithin,
  toHourMinute,
  toMonthShort,
  toWeekdayShort,
  weekdayIndex,
} from "@/shared/utils/date";
import { PRESENT_THRESHOLD, WEEK_START_DOW } from "../attendance.config";
import type { AbsenceDateRange } from "../attendance.types";
import { GridCell } from "./GridCell";
import type { CellAbsence } from "./useAbsenceTooltip";
import { useDateDragSelection } from "./useDateDragSelection";

type Player = components["schemas"]["PlayerRow"];
type Absence = components["schemas"]["AbsenceCell"];
type Raid = components["schemas"]["RaidCell"];

const absenceForDay = (p: Player, dayIso: string): Absence | null =>
  (p.absences ?? []).find((a) => isWithin(dayIso, a.startDate, a.endDate)) ??
  null;

// Raid-day column tokens, shared by DayHeaderCell and PlayerRow below so the look can't drift.
// (GridCell's own-row hover uses a neutral wash, not these — see there.)

// Faint blue wash (--raid-accent, see styles.css). Opaque (mixed with --background, not alpha) so the sticky header masks cells scrolling under it.
const RAID_TINT =
  "bg-[color-mix(in_oklab,var(--raid-accent)_16%,var(--background))]";
const RAID_TEXT = "text-[color:var(--raid-accent)]";

// Raid-day header hover: stronger than resting RAID_TINT (16%) so the hover reads.
const RAID_HOVER =
  "hover:bg-[color-mix(in_oklab,var(--raid-accent)_30%,var(--background))]";

// Own-row wash (MY_ROW_TINT) lives in @/shared/lib/rowTint — shared with the setup boards.

// Weeks are separated by an empty spacer column (width WEEK_GAP), not a drawn line — it carries no grid
// box, so the break reads as whitespace. Starts every WEEK_START_DOW; the first also divides the
// sticky name column from the grid.
const WEEK_GAP = "w-4";
const isWeekStart = (dayIso: string): boolean =>
  weekdayIndex(dayIso) === WEEK_START_DOW;

// Spacer column: <th> (sticky) in the header, <td> in body/footer.
function WeekGap({ as: Tag = "td" }: { as?: "th" | "td" }) {
  return (
    <Tag
      aria-hidden
      className={cn(
        WEEK_GAP,
        Tag === "th" ? "sticky top-0 z-20 bg-background" : "p-0",
      )}
    />
  );
}

// One day column's header: date labels, raid marker, and (officers+) a button opening the raid dialog.
function DayHeaderCell({
  dayIso,
  today,
  raid,
  showMonth,
  canManage,
  onHeaderClick,
}: {
  dayIso: string;
  today: string;
  raid: Raid | undefined;
  showMonth: boolean;
  canManage: boolean;
  onHeaderClick: (dayIso: string, raid?: Raid) => void;
}) {
  const inner = (
    <div className={cn("rounded-sm py-0.5", raid && RAID_TEXT)}>
      {/* Title/time rows always reserved (empty on non-raid days) so header height stays constant and the
          grid doesn't jump as raid columns scroll in and out of view. */}
      <div className="relative h-4">
        {raid && (
          // Absolutely positioned + centered (left-1/2, -translate-x-1/2) so a long title overflows
          // symmetrically without widening the fixed w-10 column; never wraps.
          <span className="absolute left-1/2 w-max -translate-x-1/2 whitespace-nowrap text-xs font-bold leading-4">
            {raid.title}
          </span>
        )}
      </div>
      {/* nbsp holds the line's height on non-raid days so the reserved slot matches a raid day exactly. */}
      <span className="block leading-tight text-[11px] font-semibold tabular-nums text-foreground">
        {raid ? toHourMinute(raid.startTime) : "\u00A0"}
      </span>
      <span className="block h-4 leading-4 text-[11px] font-bold">
        {showMonth ? toMonthShort(dayIso) : ""}
      </span>
      <span className="block leading-tight text-[11px]">
        {toWeekdayShort(dayIso)}
      </span>
      <span
        className={cn(
          "mt-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center px-1 leading-none",
          // Today marker: white pill around the day number.
          dayIso === today &&
            "rounded-full bg-foreground/80 font-semibold text-background",
        )}
      >
        {dayOfMonthLabel(dayIso)}
      </span>
    </div>
  );
  // Officers+ get a clickable dialog trigger; others an inert header.
  const trigger = canManage ? (
    <button
      type="button"
      className={cn(
        "block w-full",
        // Raid days hover faint blue. A 1px transparent border on the framed sides + bg-clip-padding keeps
        // the hover fill inside the box-shadow rails instead of overpainting them. Normal days get a matching
        // 1px transparent top border so the header row doesn't grow 1px when a raid column scrolls into view.
        raid
          ? cn(
              RAID_HOVER,
              "border-x border-t border-transparent bg-clip-padding rounded-t-md",
            )
          : "rounded-sm border-t border-transparent hover:bg-muted",
      )}
      onClick={() => onHeaderClick(dayIso, raid)}
    >
      {inner}
    </button>
  ) : (
    inner
  );
  return (
    <th
      className={cn(
        // w-10 (40px) must stay = DAY_COLUMN_PX; useVisibleDayCount's fit math depends on it.
        "sticky top-0 w-10 bg-background p-0 text-center align-bottom text-xs font-normal text-muted-foreground",
        // Raid headers z-[25] > normal z-20 so an overflowing title paints over neighbours' opaque bg;
        // still below the z-30 sticky name column.
        raid ? "z-[25]" : "z-20",
        raid && RAID_TINT,
        // Frame on 3 sides (bottom open) as an inset box-shadow, not a border, so it doesn't widen the raid column.
        // --raid-frame (not raw --raid-accent) so the header rail matches the body rails, which are muted by
        // each cell's grey grid border painting over them — see --raid-frame in styles.css.
        raid &&
          "rounded-t-md shadow-[inset_1px_0_0_var(--raid-frame),inset_-1px_0_0_var(--raid-frame),inset_0_1px_0_var(--raid-frame)]",
      )}
    >
      {raid ? (
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent
            side="top"
            avoidCollisions={false}
            className="max-w-[240px]"
          >
            <p className="font-medium">
              {raid.title}{" "}
              <span className="font-normal tabular-nums text-background/70">
                ({toHourMinute(raid.startTime)})
              </span>
            </p>
            {raid.description && (
              <p className="text-xs text-background/70">{raid.description}</p>
            )}
          </TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}
    </th>
  );
}

function PlayerRow({
  player: p,
  roleChanged,
  isLast,
  dates,
  raidsByDate,
  onEditOwn,
  onCreateOwn,
  onShow,
  selectedRange,
  onPointerDown,
  onPointerEnter,
  onPointerUp,
}: {
  player: Player;
  roleChanged: boolean;
  isLast: boolean;
  dates: string[];
  raidsByDate: Map<string, Raid>;
  onEditOwn: (a: CellAbsence) => void;
  onCreateOwn: (dayIso: string) => void;
  onShow: (a: CellAbsence, x: number, y: number) => void;
  selectedRange: AbsenceDateRange | null;
  onPointerDown: (
    event: ReactPointerEvent<HTMLButtonElement>,
    dayIso: string,
  ) => void;
  onPointerEnter: (
    event: ReactPointerEvent<HTMLButtonElement>,
    dayIso: string,
  ) => void;
  onPointerUp: (
    event: ReactPointerEvent<HTMLButtonElement>,
    dayIso: string,
  ) => void;
}) {
  return (
    <tr>
      <td
        className={cn(
          "sticky left-0 z-10 whitespace-nowrap pl-2 pr-6 text-sm",
          p.isMe ? MY_ROW_TINT : "bg-background",
          roleChanged && "border-t-2 border-border",
          // Grey when no class; otherwise the class colour comes from the inline style below.
          !p.wowClass && "text-muted-foreground",
        )}
        style={classColorStyle(p.wowClass)}
      >
        {/* Cap + ellipsis on mobile so a long name can't blow out the sticky column; full width at md+
            (the td keeps whitespace-nowrap). */}
        <span className="block max-w-[128px] truncate md:max-w-none">
          {p.displayName ?? p.username}
        </span>
      </td>
      {dates.map((d) => {
        const isRaid = raidsByDate.has(d);
        return (
          <Fragment key={d}>
            {isWeekStart(d) && <WeekGap />}
            <td
              className={cn(
                p.isMe && MY_ROW_TINT,
                roleChanged && "border-t-2 border-border",
                // After the my-row tint so the raid column stays uniform where they cross.
                isRaid && RAID_TINT,
                // Continue the header's blue rails down as an inset box-shadow (not a border, so the column
                // isn't widened); sides only, so the role-separator border-t is untouched. The last row adds
                // the bottom in one combined shadow (a separate bottom-only class would overwrite the sides).
                isRaid &&
                  !isLast &&
                  "shadow-[inset_1px_0_0_var(--raid-accent),inset_-1px_0_0_var(--raid-accent)]",
                isRaid &&
                  isLast &&
                  "shadow-[inset_1px_0_0_var(--raid-accent),inset_-1px_0_0_var(--raid-accent),inset_0_-1px_0_var(--raid-accent)]",
              )}
            >
              <GridCell
                dayIso={d}
                absence={absenceForDay(p, d)}
                editable={p.isMe}
                onEditOwn={onEditOwn}
                onCreateOwn={onCreateOwn}
                onShow={onShow}
                selected={Boolean(
                  p.isMe &&
                    selectedRange &&
                    isWithin(d, selectedRange.startDate, selectedRange.endDate),
                )}
                onPointerDown={onPointerDown}
                onPointerEnter={onPointerEnter}
                onPointerUp={onPointerUp}
              />
            </td>
          </Fragment>
        );
      })}
    </tr>
  );
}

// Memoized so the cursor-following tooltip's re-renders don't re-render the grid.
export const GridTable = memo(function GridTable({
  players,
  dates,
  today,
  presentByDate,
  raidsByDate,
  canManage,
  onHeaderClick,
  onEditOwn,
  onCreateOwn,
  onShow,
}: {
  players: Player[];
  dates: string[];
  today: string;
  presentByDate: Map<string, number>;
  raidsByDate: Map<string, Raid>;
  canManage: boolean;
  onHeaderClick: (dayIso: string, raid?: Raid) => void;
  onEditOwn: (a: CellAbsence) => void;
  onCreateOwn: (range: AbsenceDateRange) => void;
  onShow: (a: CellAbsence, x: number, y: number) => void;
}) {
  const ownPlayer = useMemo(
    () => players.find((player) => player.isMe),
    [players],
  );
  const selectableDates = useMemo(
    () =>
      new Set(
        ownPlayer
          ? dates.filter((date) => !absenceForDay(ownPlayer, date))
          : [],
      ),
    [dates, ownPlayer],
  );
  const isDateSelectable = useCallback(
    (date: string) => selectableDates.has(date),
    [selectableDates],
  );
  const drag = useDateDragSelection({
    dates,
    isDateSelectable,
    onSelect: onCreateOwn,
  });
  const onEditOwnAfterDrag = useCallback(
    (absence: CellAbsence) => {
      if (!drag.consumeSuppressedClick()) onEditOwn(absence);
    },
    [drag.consumeSuppressedClick, onEditOwn],
  );

  return (
    <TooltipProvider>
      <div className="overflow-x-auto">
        <table className="border-separate border-spacing-0">
          <thead>
            <tr>
              <th className="sticky left-0 top-0 z-30 bg-background px-2 text-left text-xs font-medium text-muted-foreground" />
              {dates.map((d, i) => (
                <Fragment key={d}>
                  {isWeekStart(d) && <WeekGap as="th" />}
                  <DayHeaderCell
                    dayIso={d}
                    today={today}
                    raid={raidsByDate.get(d)}
                    showMonth={i === 0 || isFirstOfMonth(d)}
                    canManage={canManage && d >= today}
                    onHeaderClick={onHeaderClick}
                  />
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {players.map((p, i) => {
              const prev = players[i - 1];
              const roleChanged = Boolean(prev && prev.raidRole !== p.raidRole);
              const isLast = i === players.length - 1;
              return (
                <PlayerRow
                  key={p.userId}
                  player={p}
                  roleChanged={roleChanged}
                  isLast={isLast}
                  dates={dates}
                  raidsByDate={raidsByDate}
                  onEditOwn={onEditOwnAfterDrag}
                  onCreateOwn={drag.onClick}
                  onShow={onShow}
                  selectedRange={drag.selection}
                  onPointerDown={drag.onPointerDown}
                  onPointerEnter={drag.onPointerEnter}
                  onPointerUp={drag.onPointerUp}
                />
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td className="sticky left-0 z-10 whitespace-nowrap bg-background pl-2 pr-6 text-sm font-medium text-muted-foreground" />
              {dates.map((d) => {
                const count = presentByDate.get(d) ?? 0;
                const ready = count >= PRESENT_THRESHOLD;
                return (
                  <Fragment key={d}>
                    {isWeekStart(d) && <WeekGap />}
                    <td className="p-0">
                      <div
                        className={cn(
                          // w-full (not w-10) so the box fills the whole column regardless of the day cell's width.
                          "flex h-9 w-full items-center justify-center text-xs font-semibold tabular-nums",
                          // Aggregate row: green = raid-ready (>= threshold), red = under-strength.
                          ready ? STATUS_GREEN : STATUS_RED,
                        )}
                      >
                        {count}
                      </div>
                    </td>
                  </Fragment>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </TooltipProvider>
  );
});
